/* eslint-disable react-hooks/exhaustive-deps */
import {
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileTextOutlined,
  HolderOutlined,
  InfoCircleOutlined,
  MoreOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Collapse,
  Divider,
  Drawer,
  Dropdown,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Select,
  Tag,
  Tooltip,
} from 'antd';
import { BLOCK_TYPES, MODULES_ITEMS, QUESTIONS_TYPES } from 'utils/constants';
import pluralize from 'pluralize';
import styles from 'styles/components/cycleModules.module.scss';
import { forwardRef, useCallback, useEffect, useState } from 'react';
import AiSuggestionWrapper from './aiSuggestionWrapper';
import { generateImprovedQuestion } from 'services/api/ai';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CycleModuleRender from './cycleModuleRender';
import { v4 as uuidv4 } from 'uuid';
import { injectQuestionNumbersToModule, isLocalhost } from 'utils/utils';
import AnimatedAIButton from './animatedAIButton';

const CycleModules = ({
  modules,
  setModules,
  showModuleSelect = false,
  showPreview = false,
  disabled = false,
}) => {
  const [activeKey, setActiveKey] = useState(['SELF']);
  const [modalAddModuleOpen, setModalAddModuleOpen] = useState(false);
  const [newModuleForm] = Form.useForm();
  const [newQuestionForm] = Form.useForm();
  const [newQuestionFormValues, setNewQuestionFormValues] = useState({});
  const [modalAddQuestionOpen, setModalAddQuestionOpen] = useState(null);
  const [aiSuggestionLoading, setAiSuggestionLoading] = useState(false);
  const [followUpQuestions, setFollowUpQuestions] = useState(null);
  const [modalAddSectionOpen, setModalAddSectionOpen] = useState(false);
  const [newSectionForm] = Form.useForm();
  const [modalAddTitleOpen, setModalAddTitleOpen] = useState(false);
  const [newTitleForm] = Form.useForm();
  const [activeId, setActiveId] = useState(null);
  const [previewModule, setPreviewModule] = useState(null);
  const [currentEditBlock, setCurrentEditBlock] = useState(null);
  const [choicesRating, setChoicesRating] = useState([]);
  const [version, setVersion] = useState('v1');

  const [toggledAISuggestion, setToggledAISuggestion] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const generateFollowUpQuestionCall = useCallback(
    (question, versionParam) => {
      setAiSuggestionLoading(true);
      generateImprovedQuestion({
        question,
        moduleType: modalAddQuestionOpen,
        version: versionParam,
      })
        .then((res) => {
          setFollowUpQuestions(res.data);
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          setAiSuggestionLoading(false);
        });
    },
    [modalAddQuestionOpen]
  );

  const addModule = (values) => {
    const newModule = MODULES_ITEMS[values.moduleType];
    const newModules = [...modules, newModule];
    setModules(newModules);
    setModalAddModuleOpen(false);
    newModuleForm.resetFields();
    setActiveKey([newModule.type]);
  };

  const addQuestion = (values) => {
    if (
      values.questionType === QUESTIONS_TYPES.MULTIPLE_CHOICE.id &&
      !values.choices
    ) {
      message.error('Please add choices');
      return;
    }
    const newModules = modules.map((m) =>
      m.type === modalAddQuestionOpen
        ? {
          ...m,
          blocks: [
            ...(currentEditBlock
              ? m.blocks.map((b) =>
                b._id === currentEditBlock._id
                  ? {
                    type: BLOCK_TYPES.QUESTION.id,
                    _id: currentEditBlock._id,
                    questionNumber:
                      m.blocks.filter(
                        (b) => b.type === BLOCK_TYPES.QUESTION.id
                      ).length + 1,
                    params: {
                      text: values.question,
                      type: values.questionType,
                      isCommentOptional:
                        values.isCommentOptional || false,
                      hasComment: values.hasComment || false,
                      isRequired: values.isRequired || false,
                      ...(values.questionType ===
                        QUESTIONS_TYPES.MULTIPLE_CHOICE.id
                        ? {
                          choices: values.choices,
                        }
                        : {}),
                      ...(values.questionType ===
                        QUESTIONS_TYPES.RATING.id
                        ? {
                          choices: choicesRating,
                        }
                        : {}),
                    },
                  }
                  : b
              )
              : [
                ...m.blocks,
                {
                  type: BLOCK_TYPES.QUESTION.id,
                  _id: `GENERATED_${uuidv4()}`,
                  questionNumber:
                    m.blocks.filter(
                      (b) => b.type === BLOCK_TYPES.QUESTION.id
                    ).length + 1,
                  params: {
                    text: values.question,
                    type: values.questionType,
                    isCommentOptional: values.isCommentOptional || false,
                    hasComment: values.hasComment || false,
                    isRequired: values.isRequired || false,
                    ...(values.questionType ===
                      QUESTIONS_TYPES.MULTIPLE_CHOICE.id
                      ? {
                        choices: values.choices,
                      }
                      : {}),
                    ...(values.questionType === QUESTIONS_TYPES.RATING.id
                      ? {
                        choices: choicesRating,
                      }
                      : {}),
                  },
                },
              ]),
          ],
        }
        : m
    );

    setModules(newModules);
    setModalAddQuestionOpen(false);
    setCurrentEditBlock(null);
    newQuestionForm.resetFields();
    setChoicesRating([]);
    setNewQuestionFormValues(null);
    setCurrentEditBlock(null);
  };

  const addDivider = (module) => {
    const newModules = modules.map((m) =>
      m.type === module.type
        ? {
          ...m,
          blocks: [
            ...m.blocks,
            { type: BLOCK_TYPES.DIVIDER.id, _id: `GENERATED_${uuidv4()}` },
          ],
        }
        : m
    );
    setModules(newModules);
  };

  const addSection = (values) => {
    const newModules = modules.map((m) =>
      m.type === modalAddSectionOpen
        ? {
          ...m,
          blocks: [
            ...(currentEditBlock
              ? m.blocks.map((b) =>
                b._id === currentEditBlock._id
                  ? {
                    type: BLOCK_TYPES.SECTION.id,
                    text: values.section,
                    _id: currentEditBlock._id,
                  }
                  : b
              )
              : [
                ...m.blocks,
                {
                  type: BLOCK_TYPES.SECTION.id,
                  text: values.section,
                  _id: `GENERATED_${uuidv4()}`,
                },
              ]),
          ],
        }
        : m
    );
    setModules(newModules);
    setModalAddSectionOpen(false);
    newSectionForm.resetFields();
    setCurrentEditBlock(null);
    setNewQuestionFormValues(null);
  };

  const addTitle = (values) => {
    const newModules = modules.map((m) =>
      m.type === modalAddTitleOpen
        ? {
          ...m,
          blocks: [
            ...(currentEditBlock
              ? m.blocks.map((b) =>
                b._id === currentEditBlock._id
                  ? {
                    type: BLOCK_TYPES.HEADER.id,
                    text: values.title,
                    _id: currentEditBlock._id,
                  }
                  : b
              )
              : [
                ...m.blocks,
                {
                  type: BLOCK_TYPES.HEADER.id,
                  text: values.title,
                  _id: `GENERATED_${uuidv4()}`,
                },
              ]),
          ],
        }
        : m
    );
    setModules(newModules);
    setModalAddTitleOpen(false);
    newTitleForm.resetFields();
    setCurrentEditBlock(null);
  };

  const handleQuestionFormValuesChange = (values, allvalues) => {
    setNewQuestionFormValues(allvalues);

    if (
      values.questionType &&
      values.questionType === QUESTIONS_TYPES.RATING.id &&
      !values.choices
    ) {
      const initChoices = [
        'Below expectations',
        'Meet expectations',
        'Exceeds expectations',
        'Greatly exceeds expectations',
        'Truly outstanding',
      ];
      setChoicesRating(initChoices);
    }
  };

  const getDisplayPerBlockType = (block) => {
    switch (block.type) {
      case BLOCK_TYPES.HEADER.id:
        return <h2>{block.text}</h2>;
      case BLOCK_TYPES.DIVIDER.id:
        return (
          <Divider
            style={
              disabled ? {} : { width: 'auto', minWidth: 'auto', flexGrow: 1 }
            }
          />
        );
      case BLOCK_TYPES.SECTION.id:
        return <div style={{ color: 'grey' }}>{block.text}</div>;
      default:
      case BLOCK_TYPES.QUESTION.id:
        return (
          <div>
            <div>
              <b>{`Question ${block.questionNumber ? `${block.questionNumber}. ` : ''
                }`}</b>
              &nbsp;
              <Tag
                color="blue"
                style={{
                  padding: '2px 4px',
                  fontSize: 12,
                }}
              >
                {QUESTIONS_TYPES[block.params.type].label}
              </Tag>
              {block.params.text}
              {block.params.isRequired && (
                <span
                  style={{
                    color: 'red',
                    fontSize: 12,
                    marginLeft: 12,
                  }}
                >
                  * Required
                </span>
              )}
            </div>
            {(block.params.type === QUESTIONS_TYPES.MULTIPLE_CHOICE.id ||
              block.params.type === QUESTIONS_TYPES.RATING.id) && (
                <>
                  <div>
                    {block.params.choices?.map((choice, index) => (
                      <Tag key={choice} style={{ whiteSpace: 'break-spaces' }}>
                        {block.params.type === QUESTIONS_TYPES.RATING.id
                          ? `${index + 1}. `
                          : ''}
                        {choice}
                      </Tag>
                    ))}
                  </div>
                  <div>
                    {block.params.hasComment && (
                      <div style={{ marginTop: 12 }}>
                        <Tag
                          color="green"
                          style={{ padding: '2px 4px', fontSize: 12 }}
                        >
                          {' '}
                          + Comment
                          {block.params.isCommentOptional
                            ? ' (Optional)'
                            : ' (Required)'}
                          &nbsp;
                          <Tooltip title="A comment section is displayed once the answer is selected">
                            <InfoCircleOutlined />
                          </Tooltip>
                        </Tag>
                      </div>
                    )}
                  </div>
                </>
              )}
          </div>
        );
    }
  };

  const items = Object.values(BLOCK_TYPES).map((type) => ({
    key: type.id,
    label: (
      <span>
        {type.icon} &nbsp;&nbsp;
        {type.label}
      </span>
    ),
    disabled: disabled,
  }));

  const handleAddBlockClick = ({ key }, module) => {
    setCurrentEditBlock(null);

    switch (key) {
      case BLOCK_TYPES.QUESTION.id:
        setModalAddQuestionOpen(module.type);
        break;
      case BLOCK_TYPES.DIVIDER.id:
        addDivider(module);
        break;
      case BLOCK_TYPES.SECTION.id:
        setModalAddSectionOpen(module.type);
        break;
      case BLOCK_TYPES.HEADER.id:
        setModalAddTitleOpen(module.type);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    setTimeout(() => {
      newQuestionForm.resetFields();
      newSectionForm.resetFields();
      newTitleForm.resetFields();
    }, 200);
  }, [
    currentEditBlock,
    modalAddQuestionOpen,
    modalAddSectionOpen,
    modalAddTitleOpen,
  ]);

  const handleDragEnd = (event, module) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const newModules = modules.map((m) => {
        if (m.type === module.type) {
          const oldIndex = m.blocks.findIndex(
            (block) => block._id === active.id
          );
          const newIndex = m.blocks.findIndex((block) => block._id === over.id);

          return {
            ...m,
            blocks: arrayMove(m.blocks, oldIndex, newIndex),
          };
        }
        return m;
      });

      const injected = newModules.map((m) => injectQuestionNumbersToModule(m));
      setModules(injected);
    }
    setActiveId(null);
  };

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
  };

  const Item = forwardRef(({ id, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={styles.block}
        style={props.style}
        {...props.attributes}
      >
        {!disabled && (
          <div className={styles.dragBlockWrapper} {...props.listeners}>
            <HolderOutlined />
          </div>
        )}
        <div className={styles.blockContent}>
          {getDisplayPerBlockType(props.block, props.module)}
        </div>
        {!disabled && (
          <div className={styles.ctaBlockWrapper}>
            <Dropdown
              menu={{
                onClick: (e) => {
                  if (e.key === 'delete') {
                    Modal.confirm({
                      title: 'Delete block',
                      content: 'Are you sure you want to delete this block?',
                      onOk: () => {
                        const newModules = modules.map((m) =>
                          m.type === props.module.type
                            ? {
                              ...m,
                              blocks: m.blocks.filter(
                                (q) => q._id !== props.block._id
                              ),
                            }
                            : m
                        );
                        const injected = newModules.map((m) =>
                          injectQuestionNumbersToModule(m)
                        );
                        setModules(injected);
                      },
                      okText: 'Yes, Delete',
                      cancelText: 'No, keep it',
                    });
                  }
                  if (e.key === 'edit') {
                    switch (props.block.type) {
                      case BLOCK_TYPES.QUESTION.id:
                        setModalAddQuestionOpen(props.module.type);
                        setCurrentEditBlock(props.block);
                        if (
                          props.block.params.type === QUESTIONS_TYPES.RATING.id
                        ) {
                          setChoicesRating(props.block.params.choices);
                        }
                        break;
                      case BLOCK_TYPES.DIVIDER.id:
                        break;
                      case BLOCK_TYPES.SECTION.id:
                        setModalAddSectionOpen(props.module.type);
                        setCurrentEditBlock(props.block);
                        break;
                      case BLOCK_TYPES.HEADER.id:
                        setModalAddTitleOpen(props.module.type);
                        setCurrentEditBlock(props.block);
                        break;
                      default:
                        break;
                    }
                  }
                  if (e.key === 'duplicate') {
                    const duplicated = {
                      ...props.block,
                      _id: `GENERATED_${uuidv4()}`,
                      ...(props.block.params
                        ? {
                          params: {
                            ...props.block.params,
                            ...(props.block.params.choices
                              ? { choices: [...props.block.params.choices] }
                              : {}),
                          },
                        }
                        : {}),
                    };
                    const newModules = modules.map((m) => {
                      if (m.type !== props.module.type) return m;
                      const index = m.blocks.findIndex(
                        (b) => b._id === props.block._id
                      );
                      const newBlocks = [...m.blocks];
                      newBlocks.splice(index + 1, 0, duplicated);
                      return { ...m, blocks: newBlocks };
                    });
                    const injected = newModules.map((m) =>
                      injectQuestionNumbersToModule(m)
                    );
                    setModules(injected);
                  }
                },
                items: [
                  {
                    key: 'edit',
                    label: (
                      <span>
                        <EditOutlined /> Edit
                      </span>
                    ),
                  },
                  {
                    key: 'duplicate',
                    label: (
                      <span>
                        <CopyOutlined /> Duplicate
                      </span>
                    ),
                  },
                  {
                    key: 'delete',
                    danger: true,
                    label: (
                      <span>
                        <DeleteOutlined /> Delete
                      </span>
                    ),
                    disabled: props.module.blocks.length === 1,
                  },
                ],
              }}
              trigger={['click']}
            >
              <Button type="link" size="small">
                <MoreOutlined />
              </Button>
            </Dropdown>
          </div>
        )}
      </div>
    );
  });

  const SortableItem = (props) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id: props.block._id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <Item
        ref={setNodeRef}
        style={style}
        attributes={attributes}
        listeners={listeners}
        block={props.block}
        module={props.module}
      />
    );
  };
  const doesntHaveAllModules = Object.values(MODULES_ITEMS).some(
    (m) => !modules?.find((mod) => mod.type === m.type)
  );

  const shouldDisplayAiSuggestion =
    newQuestionForm?.getFieldValue('question')?.length > 0 &&
    toggledAISuggestion;

  const isRating =
    newQuestionFormValues?.questionType === QUESTIONS_TYPES.RATING.id ||
    currentEditBlock?.params?.type === QUESTIONS_TYPES.RATING.id;

  const isMultipleChoice =
    newQuestionFormValues?.questionType ===
    QUESTIONS_TYPES.MULTIPLE_CHOICE.id ||
    currentEditBlock?.params?.type === QUESTIONS_TYPES.MULTIPLE_CHOICE.id;

  return (
    <div>
      {!disabled &&
        !showModuleSelect &&
        doesntHaveAllModules &&
        modules.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              style={{ marginBottom: 12, width: 160 }}
              type="primary"
              onClick={() => setModalAddModuleOpen(true)}
              icon={<PlusOutlined />}
            >
              Add Module
            </Button>
          </div>

        )}
      {modules.length > 0 ? (
        <Collapse
          onChange={(key) => {
            setActiveKey(key);
          }}
          activeKey={activeKey}
          items={modules.map((module, i) => ({
            key: `${module.type}`,
            children: (
              <div>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(e) => handleDragEnd(e, module)}
                  onDragStart={handleDragStart}
                >
                  <SortableContext
                    items={module.blocks.map((block) => block._id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {module.blocks.map((block) => (
                      <SortableItem
                        key={block._id}
                        block={block}
                        module={module}
                      />
                    ))}
                  </SortableContext>
                  <DragOverlay>
                    {activeId ? (
                      <Item
                        block={module.blocks.find((b) => b._id === activeId)}
                        module={module}
                      />
                    ) : null}
                  </DragOverlay>
                </DndContext>
                {!disabled && (
                  <div className={styles.addBlockWrapper}>
                    <Dropdown
                      trigger={['click']}
                      menu={{
                        items,
                        onClick: (e) => handleAddBlockClick(e, module),
                      }}
                    >
                      <Button
                        shape="circle"
                        size="small"
                        icon={<PlusOutlined />}
                      />
                    </Dropdown>
                  </div>
                )}
                <div className={styles.moduleActions}>
                  <div style={{ flexGrow: 1 }}></div>
                  {!showModuleSelect && !disabled && (
                    <div>
                      <Button
                        style={{ marginTop: 12, paddingLeft: 0 }}
                        type="link"
                        danger
                        onClick={() =>
                          Modal.confirm({
                            title: 'Remove Module',
                            content:
                              'Are you sure you want to remove this module?',
                            onOk: () => {
                              setModules(
                                modules.filter((m) => m.type !== module.type)
                              );
                              setActiveKey((keys) =>
                                keys.filter((k) => k !== module.type)
                              );
                            },
                            okText: 'Yes, Remove',
                            cancelText: 'No, keep it',
                          })
                        }
                        icon={<DeleteOutlined />}
                      >
                        Remove Module
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ),
            label: (
              <div className={styles.moduleNameWrapper}>
                <div className={styles.moduleName}>
                  {showModuleSelect && (
                    <Checkbox
                      className={styles.checkbox}
                      onClick={(e) => {
                        e.stopPropagation();
                        const newModules = modules.map((mod) =>
                          mod.type === module.type
                            ? { ...mod, selected: !mod.selected }
                            : mod
                        );
                        setModules(newModules);
                      }}
                      checked={module.selected}
                    />
                  )}
                  <FileTextOutlined />
                  &nbsp;
                  {module.label}
                </div>
                <div style={{ marginRight: 12 }}>
                  <Button
                    type="link"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewModule(module);
                    }}
                  >
                    Preview
                  </Button>
                </div>
                <div className={styles.questionsCount}>
                  {`${module.blocks.filter(
                    (b) => b.type === BLOCK_TYPES.QUESTION.id
                  ).length
                    } ${pluralize(
                      'question',
                      module.blocks.filter(
                        (b) => b.type === BLOCK_TYPES.QUESTION.id
                      ).length
                    )}`}
                </div>
              </div>
            ),
          }))}
          expandIconPosition={'end'}
        />
      ) : (
        <Empty
          description={
            <div>
              No modules added yet. Add a module to get started.
              <div>
                <Button
                  style={{ marginTop: 12, width: 160 }}
                  type="primary"
                  onClick={() => setModalAddModuleOpen(true)}
                  icon={<PlusOutlined />}
                >
                  Add Module
                </Button>
              </div>
            </div>
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}

      <Modal
        open={modalAddModuleOpen}
        onCancel={() => setModalAddModuleOpen(false)}
        footer={null}
      >
        <Form form={newModuleForm} layout="vertical" onFinish={addModule}>
          <Form.Item
            label="Choose the module type"
            name="moduleType"
            rules={[{ required: true, message: 'Please choose a module type' }]}
          >
            <Select
              placeholder="Select a module in the list"
              options={Object.values(MODULES_ITEMS).map((m) => ({
                label: m.label,
                value: m.type,
                disabled: modules.find((mods) => mods.type === m.type),
              }))}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Add Module
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={'Add a question'}
        open={!!modalAddQuestionOpen}
        onCancel={() => {
          setCurrentEditBlock(null);
          setFollowUpQuestions(null);
          setModalAddQuestionOpen(null);
          setNewQuestionFormValues(null);
          setChoicesRating(null);
        }}
        footer={null}
      >
        <Form
          form={newQuestionForm}
          onValuesChange={handleQuestionFormValuesChange}
          layout="vertical"
          onFinish={addQuestion}
          initialValues={{
            questionType: QUESTIONS_TYPES.OPEN_ENDED.id,
            isRequired: true,
            ...(currentEditBlock
              ? {
                question: currentEditBlock.params?.text,
                questionType: currentEditBlock.params?.type,
                isRequired: currentEditBlock.params?.isRequired,
                choices: currentEditBlock.params?.choices,
                hasComment: currentEditBlock.params?.hasComment,
                isCommentOptional: currentEditBlock.params?.isCommentOptional,
              }
              : {}),
          }}
        >
          <Form.Item
            label="Question"
            name="question"
            rules={[{ required: true, message: 'Please input the question' }]}
          >
            <Input placeholder="Write your question here..." />
          </Form.Item>
          {shouldDisplayAiSuggestion ? (
            <AiSuggestionWrapper loading={aiSuggestionLoading}>
              <div className={styles.questionExtraLeft}>🔮</div>
              <div className={styles.questionExtraRight}>
                {newQuestionForm?.getFieldValue('question')?.length > 0 ? (
                  <div className={styles.questionExtraContent}>
                    <div className={styles.questionExtraText}>
                      {followUpQuestions ? (
                        <div>
                          {followUpQuestions.injected
                            ? 'Perfect ✅'
                            : followUpQuestions.text}
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              marginTop: 14,
                            }}
                          >
                            {followUpQuestions.injected ? null : (
                              <div>
                                <Button
                                  style={{ paddingLeft: 0 }}
                                  type="link"
                                  size="small"
                                  onClick={() => {
                                    newQuestionForm.setFieldValue(
                                      'question',
                                      followUpQuestions.text
                                    );
                                    setFollowUpQuestions((f) => ({
                                      ...f,
                                      injected: true,
                                    }));
                                  }}
                                >
                                  Use this suggestion
                                </Button>
                              </div>
                            )}
                            <AnimatedAIButton
                              loading={aiSuggestionLoading}
                              onClick={() => {
                                generateFollowUpQuestionCall(
                                  newQuestionForm?.getFieldValue('question'),
                                  version
                                );
                              }}
                            >
                              Get AI feedback
                            </AnimatedAIButton>
                          </div>
                        </div>
                      ) : aiSuggestionLoading ? (
                        <span>&nbsp;Generating...</span>
                      ) : (
                        <span>
                          Start editing your question, the AI will help you
                          improve it.
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    Start writing a question and the AI will help you improve it
                  </div>
                )}
                {isLocalhost() && (
                  <Select
                    value={version}
                    name="version"
                    options={[
                      { label: 'v1', value: 'v1' },
                      { label: 'v2', value: 'v2' },
                    ]}
                    onChange={(value) => {
                      setVersion(value);
                    }}
                  />
                )}
              </div>
            </AiSuggestionWrapper>
          ) : (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <AnimatedAIButton
                onClick={() => {
                  if (!newQuestionForm?.getFieldValue('question')?.length) {
                    message.error(
                      'Please enter a question before improving with AI'
                    );
                    return;
                  } else {
                    setToggledAISuggestion(true);
                    generateFollowUpQuestionCall(
                      newQuestionForm?.getFieldValue('question'),
                      version
                    );
                  }
                }}
              >
                Get AI feedback
              </AnimatedAIButton>
            </div>
          )}
          <br />
          <Form.Item label="Type" name="questionType">
            <Select
              options={Object.values(QUESTIONS_TYPES).map((type) => ({
                label: type.label,
                value: type.id,
              }))}
            />
          </Form.Item>
          {isMultipleChoice && (
            <Form.Item
              label="Choices"
              name="choices"
              extra={'Press enter to add a choice'}
            >
              <Select
                mode="tags"
                style={{ width: '100%' }}
                placeholder="Enter choices"
                showSearch={false}
                open={false}
              />
            </Form.Item>
          )}
          {isRating && (
            <div>
              <div>
                {(choicesRating || []).map((choice, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: 10,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Input
                      prefix={<b>{`${index + 1}. `}</b>}
                      placeholder={`Choice ${index + 1}`}
                      value={choice}
                      onChange={(e) => {
                        const newchoices = [...(choicesRating || [])];
                        newchoices[index] = e.target.value;
                        setChoicesRating(newchoices);
                      }}
                    />
                    <Button
                      type={'link'}
                      icon={<CloseOutlined />}
                      style={{ color: 'grey' }}
                      onClick={() => {
                        setChoicesRating((prev) =>
                          prev.filter((_, i) => i !== index)
                        );
                      }}
                    />
                  </div>
                ))}
              </div>

              <Button
                icon={<PlusOutlined />}
                style={{ paddingLeft: 0 }}
                type="link"
                onClick={() => {
                  setChoicesRating((prev) => [...prev, `New Choice`]);
                }}
              >
                Add Choice
              </Button>
            </div>
          )}
          <Form.Item name="isRequired" valuePropName="checked">
            <Checkbox>Is this question required?</Checkbox>
          </Form.Item>
          {(isRating || isMultipleChoice) && (
            <Form.Item name="hasComment" valuePropName="checked">
              <Checkbox>
                Should the participant be able to add a comment?
              </Checkbox>
            </Form.Item>
          )}
          {(isRating || isMultipleChoice) &&
            (newQuestionFormValues?.hasComment ||
              currentEditBlock?.params?.hasComment) && (
              <Form.Item name="isCommentOptional" valuePropName="checked">
                <Checkbox>Should the comment be optional?</Checkbox>
              </Form.Item>
            )}
          <Form.Item>
            <Button type="primary" htmlType="submit">
              {currentEditBlock ? 'Update Question' : 'Add Question'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={'Add a text section'}
        open={!!modalAddSectionOpen}
        onCancel={() => {
          setCurrentEditBlock(null);
          setModalAddSectionOpen(false);
        }}
        footer={null}
      >
        <Form
          form={newSectionForm}
          layout="vertical"
          onFinish={addSection}
          initialValues={
            currentEditBlock
              ? {
                section: currentEditBlock?.text,
              }
              : {}
          }
        >
          <Form.Item
            label="Section"
            name="section"
            rules={[{ required: true, message: 'Please input the section' }]}
          >
            <Input.TextArea placeholder="Section Text" autoSize={true} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              {currentEditBlock ? 'Update Text' : 'Add Text section'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={'Add a Title'}
        open={!!modalAddTitleOpen}
        onCancel={() => {
          setModalAddTitleOpen(false);
          setCurrentEditBlock(null);
        }}
        footer={null}
      >
        <Form
          form={newTitleForm}
          layout="vertical"
          onFinish={addTitle}
          initialValues={
            currentEditBlock
              ? {
                title: currentEditBlock?.text,
              }
              : {}
          }
        >
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: 'Please input the title' }]}
          >
            <Input placeholder="Title Text" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              {currentEditBlock ? 'Update Title' : 'Add Title'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      <Drawer
        width={600}
        open={!!previewModule}
        onClose={() => setPreviewModule(null)}
        title="Preview"
      >
        {previewModule && <CycleModuleRender module={previewModule} />}
      </Drawer>
    </div>
  );
};

export default CycleModules;
